import { useMemo, useState } from 'react';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { UpstackDataGrid } from '../UpstackDataGrid/UpstackDataGrid';
import { populateColumnValueOptions } from '../UpstackDataGrid/helpers';
import { PrefKey } from '../../../store/User';
import { PermissionSet, PermissionSetAssignment } from '../../../types';
import { PERMISSION_SET_FILTERS, PermissionSetFilterKey, filterPermissionSets } from './permissionSetFilters';
import { buildPermissionSetColumns } from '../../Pages/Admin/ColumnDefs/PermissionSetsGridColumnDefs';
import { buildGridSettings } from './gridSettings';

export interface PermissionSetsListProps {
  permissionSets: PermissionSet[];
  loading: boolean;
  error: string | null;
  assignments: PermissionSetAssignment[];
  teamMemberCount?: (teamId: number) => number;
  tagNames?: Record<string, string>;
  onDelete: (set: PermissionSet) => void;
  onRetry: () => void;
}

export const PermissionSetsList = ({
  permissionSets,
  loading,
  error,
  assignments,
  teamMemberCount,
  tagNames,
  onDelete,
  onRetry
}: PermissionSetsListProps) => {
  const [filter, setFilter] = useState<PermissionSetFilterKey>('all');
  const rows = useMemo(() => filterPermissionSets(permissionSets, filter, ''), [permissionSets, filter]);
  const columns = useMemo(
    () =>
      populateColumnValueOptions(
        permissionSets,
        buildPermissionSetColumns({ assignments, teamMemberCount, tagNames, onDelete })
      ),
    [permissionSets, assignments, teamMemberCount, tagNames, onDelete]
  );
  const gridSettings = useMemo(() => buildGridSettings(columns), [columns]);
  const isEmpty = !loading && !error && permissionSets.length === 0;

  return (
    <Stack
      spacing={2}
      data-cy="permission-sets-list">
      <Stack
        direction="row"
        spacing={1}>
        {PERMISSION_SET_FILTERS.map((option) => (
          <Chip
            key={option.key}
            label={option.label}
            clickable
            color={filter === option.key ? 'primary' : 'default'}
            variant={filter === option.key ? 'filled' : 'outlined'}
            onClick={() => setFilter(option.key)}
          />
        ))}
      </Stack>

      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={onRetry}>
              Retry
            </Button>
          }>
          {error}
        </Alert>
      )}

      {isEmpty ? (
        <Typography
          color="text.secondary"
          className="py-6">
          No permission sets yet. Create one to start restricting access.
        </Typography>
      ) : (
        <UpstackDataGrid
          title="Permission Sets"
          rows={rows}
          columns={columns}
          loadingData={loading}
          page={'permission_sets_grid' as PrefKey}
          gridSettings={gridSettings}
          showSearch
          rowPrimaryKeys={['id']}
          containerClassName="min-h-[20rem]"
          dataCy="permission-sets-grid"
        />
      )}
    </Stack>
  );
};
